import { createContext } from "react";

import type { TitleBundle } from "./types";

/** How a reader asked for titles to be shown. Mirrors the profile field. */
export type TitleDisplayPreference = "english" | "native" | "romanized";

/**
 * The viewer's saved title preference.
 *
 * Published by `ProfileGate` for the same reason the formatting locale is: it
 * is the one component above every surface that renders a title and already
 * holds the profile. `undefined` means no saved preference reached this
 * subtree -- signed out, or an isolated test -- and the default applies.
 */
export const TitlePreferenceContext = createContext<TitleDisplayPreference | undefined>(undefined);

/**
 * The title to lead with, and the one to show beneath it.
 *
 * The preference was collected at onboarding, editable in settings and
 * round-tripped through Convex while every catalogue surface rendered
 * `titles.primary` regardless -- a reader choosing Native or Romanized saw no
 * change anywhere. This is what the setting means.
 *
 * Falls through rather than showing nothing: a show with no romanization still
 * has to appear, and for most Western titles all three fields are the same
 * string. The secondary is whichever remaining title actually differs, so a
 * card never repeats itself.
 */
export function titlesFor(
  titles: TitleBundle,
  preference: TitleDisplayPreference | undefined,
): { primary: string; secondary: string | null } {
  const order =
    preference === "native"
      ? [titles.native, titles.romanized, titles.english]
      : preference === "romanized"
        ? [titles.romanized, titles.native, titles.english]
        : [titles.english, titles.romanized, titles.native];

  const primary = order.find((title) => title !== null && title !== "") ?? titles.primary;
  const secondary =
    [titles.native, titles.romanized, titles.english].find(
      (title) => title !== null && title !== "" && title !== primary,
    ) ?? null;
  return { primary, secondary };
}
